import type {RepositoryVisibility} from '../repository-picker-types'

export type RepositoryPickerOwner = {
  login: string
  type: 'organization' | 'user'
}

export type RepositorySearchQueryOptions = {
  owner?: RepositoryPickerOwner
  organization?: string
  excludeRepo?: string
  includeForks?: boolean
  repositoryVisibility?: RepositoryVisibility
  /**
   * Opt-in; archived repositories are excluded by default.
   *
   * The default exists because this helper was written for choosing a repository to create an
   * issue in, where an archived repository is not a valid destination. A filter that only scopes
   * a search has no such restriction, so it can ask for archived repositories to be kept.
   */
  includeArchived?: boolean
}

/** Qualifiers that could move a search outside the owner it is scoped to. */
const SCOPE_QUALIFIER = /^-?(?:org|owner|user|repo):/i

function buildBaseQuery(options?: RepositorySearchQueryOptions): string {
  let query = options?.includeArchived ? 'in:name' : 'in:name archived:false'
  if (options?.excludeRepo) {
    query = `${query} -repo:${options.excludeRepo}`
  }

  if (options?.includeForks) {
    query = `${query} fork:true`
  }

  if (options?.repositoryVisibility && options.repositoryVisibility !== 'PUBLIC') {
    // If the repository visibility is public, we don't need to add it to the query.
    // If it is either internal or private, we need to exclude the public repositories from the query.
    query = `${query} -visibility:public`
  }

  return query
}

// Attempt to use the `org` parameter to filter the results if we have a `/` present in the query
// This is to be used with the `search` graphQL endpoint for type `Repository` only.
export function getRepositorySearchQuery(queryString: string, options?: RepositorySearchQueryOptions): string {
  const orgIndexSplit = queryString.indexOf('/')

  const query = buildBaseQuery(options)

  if (options?.owner) {
    const qualifier = options.owner.type === 'organization' ? 'org' : 'user'
    return `${query} ${qualifier}:${options.owner.login} ${queryString}`
  }
  // if there is <owner>/<repo> in the query, use that to filter the results
  if (orgIndexSplit > 0 && queryString.split('/').length === 2) {
    // Naively extract the org name and repo name from the query by assuming anything before the / is the org name
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const orgSearchName = queryString.split('/')[0]!
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const repoSearchName = queryString.split('/')[1]!
    return `${query} org:${orgSearchName} ${repoSearchName}`
  }

  // If there is an org name, use that to filter the results
  if (options?.organization) {
    return `${query} org:${options.organization} ${queryString}`
  }

  // If there is no / in the query, or the org name is empty, just search for the repo name
  return `${query} ${queryString}`
}

export type ScopedRepositorySearch = {
  /** The phrase sent to the search endpoint. */
  searchQuery: string
  /** `owner/name` the text named exactly, when it names one inside the scope. */
  exactNameWithOwner?: string
  /** Text that was dropped because it could have moved the search outside the scope. */
  removedQualifiers: string[]
}

/**
 * Builds a search phrase for a repository picker that is pinned to a single owner.
 *
 * Unlike {@link getRepositorySearchQuery}, this understands that the typed text belongs to an
 * owner that is already decided:
 *
 * - `owner/name` is read as naming a repository rather than searched literally, so typing the
 *   full `github/github` finds the repository called `github` instead of searching names for the
 *   string `github/github`, which matches nothing.
 * - Qualifiers that could redirect the search to another owner are removed from the typed text
 *   rather than concatenated after the scope qualifier.
 *
 * The removal is a correctness measure, not a security boundary: this endpoint builds its scope
 * from a client-side string, so callers must still check the owner of every returned repository.
 */
export function getScopedRepositorySearchQuery(
  queryString: string,
  options: {owner: RepositoryPickerOwner; includeArchived?: boolean},
): ScopedRepositorySearch {
  const {owner, includeArchived} = options
  const ownerLogin = owner.login.toLowerCase()
  const removedQualifiers: string[] = []

  const terms = queryString
    .trim()
    .split(/\s+/)
    .filter(term => term.length > 0)
    .filter(term => {
      if (!SCOPE_QUALIFIER.test(term)) return true
      removedQualifiers.push(term)
      return false
    })

  let exactNameWithOwner: string | undefined
  const text = terms
    .map(term => {
      const separator = term.indexOf('/')
      if (separator <= 0 || term.indexOf('/', separator + 1) !== -1) return term

      const typedOwner = term.slice(0, separator)
      const name = term.slice(separator + 1)
      if (!name || typedOwner.toLowerCase() !== ownerLogin) return term

      exactNameWithOwner = `${owner.login}/${name}`
      return name
    })
    .join(' ')

  const qualifier = owner.type === 'organization' ? 'org' : 'user'
  const base = buildBaseQuery({includeArchived})
  const searchQuery = text ? `${base} ${qualifier}:${owner.login} ${text}` : `${base} ${qualifier}:${owner.login}`

  // A single bare word names a repository inside the scope just as much as `owner/name` does, so
  // it gets the same exact-match treatment. That matters where the name is also a common prefix:
  // `github` inside the `github` organisation matches hundreds of repositories, and without this
  // the one actually called `github` can sit well past the first page of results.
  if (!exactNameWithOwner && terms.length === 1 && text && !text.includes('/') && !text.includes(':')) {
    exactNameWithOwner = `${owner.login}/${text}`
  }

  return {searchQuery, exactNameWithOwner, removedQualifiers}
}
